import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  Alert,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Spinner,
  Badge,
} from "react-bootstrap";
import { useHistory, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { getUserFromLocalStorage } from "../../utilities/user.js";
import usePersistedPreference from "../../hooks/usePersistedPreference";
import { listViewDefaults } from "../../config/listViewDefaults";
import { getTokenFromLocalStorage } from "../../utilities/tokenUtils.js";
import {
  MdEdit,
  MdDelete,
  MdAdd,
  MdShoppingBag,
  MdExpandMore,
  MdErrorOutline,
  MdSearchOff,
  MdDragIndicator,
} from "react-icons/md";
import { FaShoppingBag } from "react-icons/fa";
import ReadOnlyQuill from "../ReadOnlyQuill.js";
import placeholderImage from "../../assets/img/menuImage.png";
import { FaFileImport, FaDownload } from "react-icons/fa";
import { Switch } from "@material-ui/core";
import { apiSetCatalogItemStatus } from "../../api";
import ProductSimulation from "../../components/ProductSimulation";
import BrandImportModal from "../Components/BrandImportModal";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useGate, Gated, GATE_TITLE } from "../../components/team/Gated";
import SearchBox, { SearchHits } from "../../components/SearchBox";
import {
  needsPhoto,
  photoTotals,
  photoFilterTest,
  NeedsPhotoBadge,
  PhotoFilterChips,
} from "../../components/NeedsPhoto";
import { recall, remember } from "../../utilities/identityMemory";
import BranchAvailabilityGrid from "../../components/branch/BranchAvailabilityGrid";
import BranchOffChip, { useBranchOffCounts } from "../../components/branch/BranchOffChip";
import BranchMemberNote from "../../components/branch/BranchMemberNote";
import { useVendorLocations } from "../../context/BranchScopeContext";
import { vendorOwnerIdOf } from "../../utilities/vendorScope";

/* This tab's last brand, kept per signed-in identity (see the component). */
export const BRAND_MEMORY_KEY = "lusuku.brandProductManagement.lastBrand";

/* Blank workbook served from public/templates. Linked from the page header as
   well as from inside the import modal: an operator preparing a brand's
   catalogue needs the file BEFORE they have anything to upload, and making
   them open an import dialog to find it is a step that buys nothing. */
const TEMPLATE_URL = `${
  process.env.PUBLIC_URL || ""
}/templates/Lusuku_Brand_Import_Template.xlsx`;

const API_URL = process.env.REACT_APP_SERVER_URL || "http://localhost:5000";

const customFontsCSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

.bpm-img-wrap {
  position: relative;
  display: inline-block;
  flex-shrink: 0;
}

.bpm-img-wrap .bpm-img-popover {
  display: none;
  position: absolute;
  left: 60px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1050;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(0,0,0,0.18);
  padding: 6px;
  width: 220px;
  pointer-events: none;
}

.bpm-img-wrap .bpm-img-popover img {
  width: 100%;
  height: 200px;
  object-fit: contain;
  border-radius: 8px;
  background: #f8fafc;
}

.bpm-img-wrap:hover .bpm-img-popover {
  display: block;
}


.menu-card-adorned {
  animation: fadeIn 0.5s ease-out;
  border: none !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
  border-radius: 16px !important;
  /* clip, not hidden: hidden makes the card a scroll container, which pins
     the sticky toolbar below to the card instead of the page. clip keeps
     the rounded corners and lets the toolbar follow the page scroll. */
  overflow: hidden;
  overflow: clip;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
}

.menu-header-adorned {
  background: #0a578bff !important;
  color: white !important;
  padding: 24px !important;
  border: none !important;
  border-radius: 16px 16px 0 0 !important;
}

.accordion-item-adorned {
  border: 1px solid #e9ecef !important;
  border-radius: 12px !important;
  margin-bottom: 12px !important;
  overflow: hidden;
  transition: all 0.3s ease;
  animation: slideIn 0.4s ease-out;
}

.accordion-item-adorned:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes slideIn {
  from { opacity: 0; transform: translateX(-20px); }
  to { opacity: 1; transform: translateX(0); }
}

/* Tab strip. Underline rather than the black filled pills the vendor menu uses
   — those wrap into a staircase at narrow widths, and this bar sits directly
   under a saturated blue header where more solid colour would fight it. */
.bpm-tabs {
  display: flex;
  gap: 4px;
  padding: 0 24px;
  background: #fff;
  border-bottom: 1px solid #e9edf1;
  overflow-x: auto;
  scrollbar-width: none;
}
.bpm-tabs::-webkit-scrollbar { display: none; }
.bpm-tabs button {
  border: 0;
  background: transparent;
  padding: 13px 16px;
  font-family: 'Inter', sans-serif;
  font-size: 0.86rem;
  font-weight: 600;
  color: #7b8794;
  cursor: pointer;
  white-space: nowrap;
  border-bottom: 2.5px solid transparent;
  margin-bottom: -1px;
  transition: color 0.15s, border-color 0.15s;
}
.bpm-tabs button:hover { color: #17385a; }
.bpm-tabs button.is-on { color: #17385a; border-bottom-color: #17385a; }

/* Header actions. One shape for both buttons so Import and Add Section read as
   peers — importing a catalogue is not a lesser way to add products. */
.bpm-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.bpm-header-btn {
  font-weight: 600;
  font-size: 0.86rem;
  padding: 8px 15px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  font-family: 'Inter', sans-serif;
  transition: background-color .18s ease, color .18s ease, border-color .18s ease;
}
.bpm-header-btn--primary {
  background-color: #fff; color: #133454;
  border: 1px solid #fff;
  box-shadow: 0 2px 8px rgba(0,0,0,0.14);
}
.bpm-header-btn--primary:hover, .bpm-header-btn--primary:focus { background-color: #eaf0f7; color: #133454; border-color: #eaf0f7; }
.bpm-header-btn--ghost {
  background-color: transparent; color: rgba(255,255,255,0.94);
  border: 1px solid rgba(255,255,255,0.45); box-shadow: none;
}
.bpm-header-btn--ghost:hover, .bpm-header-btn--ghost:focus { background-color: rgba(255,255,255,0.14); color: #fff; border-color: #fff; }

.bpm-header-link {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 0.76rem; font-weight: 600;
  color: rgba(255,255,255,0.72);
  padding: 8px 6px;
  text-decoration: none; white-space: nowrap;
  font-family: 'Inter', sans-serif;
  border-bottom: 1px solid transparent;
}
.bpm-header-link:hover { color: #fff; text-decoration: none; border-bottom-color: rgba(255,255,255,0.6); }

/* Toolbar: what is in this catalogue, and how to find one row in seventy.
   It stays pinned while the sections scroll underneath, so the search box
   and the filter chips are always one glance away in a 350-row catalogue.
   Sits below the impersonation and attention banners. Their measured heights
   track wrapping and disappear when a banner is hidden. */
.bpm-toolbar {
  position: sticky;
  top: calc(var(--lusuku-impersonation-h, 0px) + var(--lusuku-attention-h, 0px));
  z-index: 30;
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  padding: 12px 24px;
  border-bottom: 1px solid #e9ecef;
  background: #fbfcfd;
  box-shadow: 0 6px 14px -10px rgba(15, 23, 42, 0.25);
  font-family: 'Inter', sans-serif;
}
.bpm-counts { display: flex; align-items: baseline; gap: 10px; font-size: 0.82rem; color: #64748b; flex-wrap: wrap; }
.bpm-counts b { color: #17385a; font-weight: 700; font-variant-numeric: tabular-nums; }
.bpm-count-sep { color: #cbd5e1; }
/* The filter chips wrap as one group, so Active and Deactivated never end up
   on different lines. */
.bpm-filters { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
/* The search is the toolbar's main control, one box to find one row in
   seventy, so it takes the most room. Its look is the shared SearchBox. */
.bpm-search-wrap { flex: 1 1 300px; max-width: 480px; min-width: 240px; }
.bpm-tool-btn {
  font-size: 0.76rem; font-weight: 600;
  border: 1px solid #dbe2ea; background: #fff; color: #475569;
  border-radius: 8px; padding: 6px 11px; white-space: nowrap;
  transition: all .18s ease;
}
.bpm-tool-btn:hover:not(:disabled) { border-color: #17385a; color: #17385a; }
.bpm-tool-btn:disabled { opacity: .45; cursor: default; }
.bpm-tool-btn.is-on { background: #17385a; border-color: #17385a; color: #fff; }
/* Hovering a chip that is on must not turn its label navy on navy. */
.bpm-tool-btn.is-on:hover:not(:disabled) { background: #0f2a45; border-color: #0f2a45; color: #fff; }

/* The "Needs photo" pill used to be styled here, as .bpm-nophoto. It moved
   into components/NeedsPhoto.js alongside the rule that decides when to show
   it, so the pharmacy console gets the identical mark rather than a second
   copy of the same declarations free to drift away from this console's. */
.bpm-sku {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.7rem; color: #94a3b8; letter-spacing: .01em;
}

/* Arranging: a grip on every row, the row goes translucent while it is being
   carried, and a navy line marks where it will land. Rows keep their own
   inline layout; these classes only add the drag states. */
.bpm-row { position: relative; transition: opacity .15s ease; }
.bpm-row.is-dragging { opacity: .35; }
.bpm-row.drop-before::before, .bpm-row.drop-after::after {
  content: ""; position: absolute; left: 0; right: 0; height: 3px;
  background: #17385a; border-radius: 2px; pointer-events: none;
}
.bpm-row.drop-before::before { top: -6px; }
.bpm-row.drop-after::after { bottom: -7px; }
.bpm-grip {
  width: 26px; height: 36px; flex-shrink: 0; margin-right: 6px; padding: 0;
  border: none; border-radius: 6px; background: transparent; color: #94a3b8;
  display: inline-flex; align-items: center; justify-content: center;
  cursor: grab; transition: background-color .15s ease, color .15s ease;
}
.bpm-grip:hover, .bpm-grip:focus-visible { background: #eef2f6; color: #17385a; outline: none; }
.bpm-grip:active { cursor: grabbing; }
.bpm-grip:disabled { cursor: not-allowed; opacity: .35; background: transparent; color: #94a3b8; }
.bpm-order-note {
  font-size: 0.74rem; color: #64748b; margin: -4px 0 10px;
  font-family: 'Inter', sans-serif;
}
.bpm-order-note b { color: #17385a; font-weight: 600; }

/* Nothing on this page may sit past the right edge of its card. The card and
   every section clip their overflow (for the rounded corners), so a control
   pushed off the edge on a phone was not scrollable, it was simply gone: Add
   product, Delete section, Import and Add Section were all unreachable on a
   360px screen (4 Oct 2026). Each row below therefore wraps when it runs out
   of room, at any width, instead of relying on a breakpoint to guess. */

/* Page header: title and actions side by side while they fit, actions on
   their own line underneath when they do not. */
.bpm-head { flex-wrap: wrap; gap: 14px 20px; }
.bpm-head-text { flex: 1 1 260px; min-width: 0; }

/* Section header: the same rule. The name keeps at least 220px; below that the
   actions drop under it and wrap among themselves. */
.bpm-sec-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px 12px;
  padding: 16px 20px;
  background-color: #fff;
  border-bottom: 1px solid #e9ecef;
  font-family: 'Inter', sans-serif;
}
.bpm-sec-toggle {
  flex: 1 1 220px;
  min-width: 0;
  display: flex;
  align-items: center;
  cursor: pointer;
}
.bpm-sec-chevron { flex-shrink: 0; margin-right: 12px; transition: transform .3s ease; }
.bpm-sec-chevron.is-open { transform: rotate(180deg); }
.bpm-sec-title { min-width: 0; font-size: 16px; font-weight: 600; overflow-wrap: anywhere; }
.bpm-sec-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  margin-left: auto;
}

/* Edit and delete section. One class instead of two copies of the same inline
   styles and mouse handlers. The icon turns white on hover; before, the delete
   icon was red on a red hover and vanished. Hover effects only where a real
   pointer exists, so a tap on a phone does not leave the button stuck scaled. */
.bpm-icon-btn {
  width: 36px; height: 36px; flex-shrink: 0; padding: 0;
  display: inline-flex; align-items: center; justify-content: center;
  border: none; border-radius: 8px;
  background-color: #f8f9fa; color: inherit;
  cursor: pointer;
  transition: background-color .2s ease, color .2s ease, transform .2s ease;
}
.bpm-icon-btn--danger { color: #FB404B; }
.bpm-icon-btn:focus-visible { outline: 2px solid #17385a; outline-offset: 2px; }
@media (hover: hover) {
  .bpm-icon-btn:hover { background-color: #17385a; color: #fff; transform: scale(1.1); }
  .bpm-icon-btn--danger:hover { background-color: #FB404B; }
}

/* Product row: picture and words on the left, price and switch on the right.
   The words give way first (long names and unbroken strings wrap), and when
   even that leaves too little room the price and switch move under the words,
   still on the right, instead of being pushed off the card. */
.bpm-row { flex-wrap: wrap; gap: 8px 12px; }
.bpm-row-main { flex: 1 1 240px; min-width: 0; }
.bpm-row-text { min-width: 0; overflow-wrap: anywhere; }
.bpm-row-side { flex: 0 0 auto; margin-left: auto; gap: 6px; }

.menu-card-adorned .bpm-body { padding: 24px; }

@media (max-width: 768px) {
  .bpm-header-btn span { display: inline; }
  .bpm-search-wrap { order: -1; flex-basis: 100%; max-width: none; min-width: 0; }
}

/* Phones: give the content back the width the desktop padding was taking. */
@media (max-width: 576px) {
  .menu-header-adorned { padding: 18px 16px !important; }
  .bpm-tabs { padding: 0 12px; }
  .bpm-toolbar { padding: 12px 16px; }
  .menu-card-adorned .bpm-body { padding: 12px; }
  .bpm-sec-head { padding: 14px; }
  .menu-card-adorned .bpm-body .accordion-body { padding: 12px 14px; }
}

/* Section bulk controls: quiet until you need them, never mistakable for the
   destructive delete button beside them. */
.bpm-bulk-btn {
  font-size: 0.74rem;
  font-weight: 600;
  white-space: nowrap;
  border-radius: 8px;
  padding: 6px 11px;
  background: #fff;
  font-family: 'Inter', sans-serif;
  transition: all 0.2s ease;
}
.bpm-bulk-btn:disabled { opacity: 0.5; cursor: progress; }
.bpm-bulk-btn--on  { border: 1px solid #bbf7d0; color: #1e7e34; }
.bpm-bulk-btn--on:hover:not(:disabled)  { background: #1e9e63; border-color: #1e9e63; color: #fff; }
.bpm-bulk-btn--off { border: 1px solid #e2e8f0; color: #64748b; }
.bpm-bulk-btn--off:hover:not(:disabled) { background: #64748b; border-color: #64748b; color: #fff; }
`;

/* One answer to "is this product online?", the same one the storefront gives
   (brandB2BPublic keeps products whose enabled is not false). The counts, the
   Active / Deactivated filter, the section bulk buttons and every row's switch
   all read it, so none of them can disagree about a product. */
const isProductActive = (product) => !!product && product.enabled !== false;

/* Why the drag grips are off while a search or filter is on. */
const ARRANGE_BLOCKED_HINT = "Clear the search and filters to arrange products";

/* The status filter. One of these can be on at a time; neither on shows all. */
const STATUS_FILTERS = [
  {
    key: "active",
    label: "Active",
    title: "Show only products shoppers can see",
    test: isProductActive,
  },
  {
    key: "deactivated",
    label: "Deactivated",
    title: "Show only products hidden from shoppers",
    test: (product) => !isProductActive(product),
  },
];

/* "Is this cover a stand-in?", the badge that says so and the two chips that
   filter on it now live in components/NeedsPhoto.js, because the healthcare
   console needs the same answer and the shared catalogue import modal
   promises the operator that answer for BOTH verticals. Re-exported here so
   this module's public surface, and the test that pins the rule, are
   unchanged by the move.

   The chips stay orthogonal to the status chips above: active/deactivated
   AND photographed/not is a real and useful pair of questions. */
export { needsPhoto };

const CustomAccordionHeader = ({
  section,
  activeKey,
  handleEditSection,
  handleDeleteSection,
  handleAddProductToSection,
  onToggle,
  onBulkStatus,
  bulkBusy,
  canEdit = true,
  canActivate = true,
  activateTitle = GATE_TITLE,
}) => {
  const products = section.products || [];
  const activeCount = products.filter(isProductActive).length;
  const allOn = products.length > 0 && activeCount === products.length;
  const allOff = activeCount === 0;
  return (
    <div className="accordion-header-custom bpm-sec-head">
      <div
        className="bpm-sec-toggle"
        onClick={() => onToggle(String(section._id))}
      >
        <MdExpandMore
          size={24}
          className={`bpm-sec-chevron${
            activeKey.includes(String(section._id)) ? " is-open" : ""
          }`}
        />
        <span className="bpm-sec-title">
          {section.title}
          <Badge
            bg="primary"
            style={{
              marginLeft: "12px",
              background: "#17385aff",
              borderRadius: "12px",
              padding: "4px 12px",
              fontSize: "12px",
            }}
          >
            {products.length} product{products.length === 1 ? "" : "s"}
          </Badge>
          {/* How much of this section a shopper can actually see. A bare
              product count says nothing after a bulk import lands everything
              deactivated — which is when you most need to know. */}
          {products.length > 0 && (
            <Badge
              bg={allOff ? "secondary" : allOn ? "success" : "warning"}
              style={{
                marginLeft: "8px",
                borderRadius: "12px",
                padding: "4px 12px",
                fontSize: "12px",
                background: allOff ? "#94a3b8" : allOn ? "#1e9e63" : "#d97706",
              }}
              title={
                allOn
                  ? "Every product in this section is visible to shoppers"
                  : allOff
                  ? "Nothing in this section is visible to shoppers"
                  : `${activeCount} of ${products.length} products are visible to shoppers`
              }
            >
              {allOn
                ? "All active"
                : allOff
                ? "None active"
                : `${activeCount} of ${products.length} active`}
            </Badge>
          )}
        </span>
      </div>
      <div className="bpm-sec-actions">
        {/* A bulk-imported section is 30 products deep and lands entirely
            deactivated. Flipping it one switch at a time is not a workflow. */}
        {products.length > 0 && (
          <>
            {!allOn && (
              <button
                type="button"
                className="bpm-bulk-btn bpm-bulk-btn--on"
                disabled={bulkBusy || !canActivate}
                onClick={(e) => {
                  e.stopPropagation();
                  onBulkStatus(section, true);
                }}
                title={canActivate ? `Make all ${products.length} products in "${section.title}" visible to shoppers` : activateTitle}
              >
                Activate all
              </button>
            )}
            {!allOff && (
              <button
                type="button"
                className="bpm-bulk-btn bpm-bulk-btn--off"
                disabled={bulkBusy || !canActivate}
                onClick={(e) => {
                  e.stopPropagation();
                  onBulkStatus(section, false);
                }}
                title={canActivate ? `Hide all ${products.length} products in "${section.title}" from shoppers` : activateTitle}
              >
                Deactivate all
              </button>
            )}
          </>
        )}
        {canEdit && (
          <>
        <button
          type="button"
          className="bpm-icon-btn"
          aria-label={`Edit section ${section.title}`}
          title="Edit section"
          onClick={(e) => {
            e.stopPropagation();
            handleEditSection(
              section._id,
              section.title,
              section.description,
              section.brandId,
            );
          }}
        >
          <MdEdit size="20px" />
        </button>
        <button
          type="button"
          className="bpm-icon-btn bpm-icon-btn--danger"
          aria-label={`Delete section ${section.title}`}
          title="Delete section"
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteSection(section._id);
          }}
        >
          <MdDelete size="20px" />
        </button>
        <Button
          size="sm"
          className="btn-adorned"
          style={{
            background: "#083860",
            color: "white",
            border: "none",
            fontWeight: "600",
            padding: "6px 16px",
            whiteSpace: "nowrap",
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleAddProductToSection(e, section._id, section.brandId);
          }}
        >
          <MdAdd
            size={16}
            style={{ marginRight: "4px", verticalAlign: "middle" }}
          />
          Add product
        </Button>
          </>
        )}
      </div>
    </div>
  );
};

const BrandProductManagement = () => {
  const history = useHistory();
  const location = useLocation();
  const token = getTokenFromLocalStorage();
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
  /* Which brand this page is showing survives a refresh.
     It used to live only in router state, so arriving from Manage Brands worked
     and pressing F5 did not: the fallback looks up brands OWNED BY the signed-in
     user, and Lusuku staff own none, so a reload bounced them to the dashboard
     under "No brand found for your account" — which is not even true, the brands
     are there. Session storage keeps it to this tab, so two tabs can sit on two
     different brands.

     The memory belongs to the person signed in, not to the tab (4 Oct 2026). It
     used one fixed key, so a super admin who had been on China Town and then
     signed in as Yalelo in the same tab got China Town's catalogue under
     Yalelo's session. Keyed by identity, a vendor session never sees the
     admin's last brand and falls through to the lookup of its own; the admin
     finds theirs again on returning. See utilities/identityMemory.js. */
  const remembered = location.state?.brandId
    ? null
    : recall(BRAND_MEMORY_KEY, { storage: "session" });

  const [resolvedBrandId, setResolvedBrandId] = useState(
    location.state?.brandId || remembered?.brandId || null,
  );
  const [resolvedBrandName, setResolvedBrandName] = useState(
    location.state?.brandName || remembered?.brandName || "",
  );
  const [resolvingBrand, setResolvingBrand] = useState(
    !(location.state?.brandId || remembered?.brandId),
  );
  const brandId = resolvedBrandId;
  const brandName = resolvedBrandName || "Brand";

  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeKey, setActiveKey] = useState([]);
  // This screen had no tabs at all; "products" keeps the existing accordion as
  // the default view so nothing moves for someone who just wants to edit.
  const [activeTab, setActiveTab] = useState("products");

  /* Branches (4 Oct 2026). The branch API is keyed by the brand's OWNER: a
     vendor login works for its own business (a team member for its parent),
     and platform staff read the owner off the brand they opened. */
  const sessionUser = getUserFromLocalStorage();
  const isSellerSession = sessionUser?.result?.userType === "SELLER";
  const [brandOwnerId, setBrandOwnerId] = useState(() => (isSellerSession ? vendorOwnerIdOf(sessionUser) : null));
  const branchDirectory = useVendorLocations(brandOwnerId);
  const branchOff = useBranchOffCounts(brandOwnerId);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [bulkBusySection, setBulkBusySection] = useState(null);
  const [loadError, setLoadError] = useState("");
  // The section whose "Deactivate all" is awaiting confirmation. null = closed.
  const [bulkConfirm, setBulkConfirm] = useState(null);
  const [query, setQuery] = useState("");
  /* The chips persist, per signed-in user. They were plain state, and the one
     thing anyone does from here, opening a product, unmounts this page: coming
     back put every chip off again ("Has photo doesn't persist", 2 Oct 2026).
     The first-ever value comes from config/listViewDefaults by role, so a
     supervisor can be started on the view their job needs; after that their
     own last choice wins. The search box deliberately stays per visit. */
  const viewDefaults = listViewDefaults(
    "brandProducts",
    getUserFromLocalStorage()?.result?.role
  );
  // null shows every product; otherwise a PHOTO_FILTERS key.
  const [photoFilter, setPhotoFilter] = usePersistedPreference(
    "brandProducts.photoFilter",
    viewDefaults.photoFilter,
    { perUser: true }
  );
  // null shows every product; otherwise a STATUS_FILTERS key.
  const [statusFilter, setStatusFilter] = usePersistedPreference(
    "brandProducts.statusFilter",
    viewDefaults.statusFilter,
    { perUser: true }
  );
  // Expand state is decided ONCE per brand, then left to the operator. Re-deciding
  // it on every refetch would slam shut every section they had just opened.
  const autoExpandDone = useRef(false);

  const [sectionTitle, setSectionTitle] = useState("");
  const [sectionDescription, setSectionDescription] = useState("");
  const [currentSectionId, setCurrentSectionId] = useState(null);

  useEffect(() => {
    if (resolvedBrandId) {
      // Private browsing or storage disabled: remember() gives up quietly and
      // the page still works.
      remember(
        BRAND_MEMORY_KEY,
        { brandId: resolvedBrandId, brandName: resolvedBrandName },
        { storage: "session" },
      );
      fetchSections();
      return;
    }
    const user = getUserFromLocalStorage();
    const userId = user?.result?._id;
    const token = user?.token;
    if (!userId) {
      toast.error("No brand selected. Redirecting...");
      history.push("/admin/brands-management");
      return;
    }
    axios
      .get(`${API_URL}/api/brands`, {
        params: { vendorId: userId },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then((res) => {
        const brands = res.data?.data || res.data || [];
        const first = Array.isArray(brands) ? brands[0] : null;
        if (!first) {
          /* This lookup asks for brands OWNED BY the signed-in user, so an
             empty result is the normal case for Lusuku staff — it means "you
             own no brands", never "there are no brands". Sending them to the
             brand list to choose one is the honest answer; the old message
             plus a bounce to the dashboard was neither true nor useful. */
          toast.info("Choose which brand's products you want to manage.");
          history.push("/admin/brands-management");
          return;
        }
        setResolvedBrandId(String(first._id));
        setResolvedBrandName(first.tradingName || first.name || "Brand");
        setResolvingBrand(false);
      })
      .catch(() => {
        toast.error("Could not reach the server to load your brands.");
        history.push("/admin/brands-management");
      });
  }, [resolvedBrandId, resolvedBrandName, history]);

  /* Opening every section was fine for a brand with two of them. A bulk-imported
     brand has twelve and seventy products, and expanding all of it puts the
     twelfth section thousands of pixels below the fold. Few sections still open
     flat; many start closed. Decided once, then it is the operator's to change. */
  const AUTO_EXPAND_LIMIT = 3;
  useEffect(() => {
    if (!Array.isArray(sections) || sections.length === 0) {
      setActiveKey([]);
      autoExpandDone.current = false;
      return;
    }
    if (autoExpandDone.current) return;
    autoExpandDone.current = true;
    setActiveKey(
      sections.length <= AUTO_EXPAND_LIMIT
        ? sections.map((section) => String(section._id))
        : [],
    );
  }, [sections]);

  useEffect(() => {
    // A new brand gets a fresh decision.
    autoExpandDone.current = false;
  }, [brandId]);

  useEffect(() => {
    if (isSellerSession || !brandId) return undefined;
    let live = true;
    axios
      .get(`${API_URL}/api/brands/${brandId}`, { headers: authHeaders, timeout: 20000 })
      .then(({ data }) => {
        const owner = data?.data?.owner;
        if (live) setBrandOwnerId(owner ? String(owner._id || owner) : null);
      })
      // Without the owner the Branches tab stays hidden; nothing else needs it.
      .catch(() => live && setBrandOwnerId(null));
    return () => {
      live = false;
    };
  }, [brandId, isSellerSession]);

  const fetchSections = async () => {
    // Without this the spinner runs forever when brand resolution yields nothing:
    // `loading` starts true and only the request path ever clears it.
    if (!brandId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError("");
    try {
      /* MUST be authenticated. GET /:id/sections is a public route and, since
         Aug 2026, a viewer-aware one: an anonymous caller is served only the
         ENABLED products of an ACTIVE brand. Managing a catalogue means seeing
         the deactivated items in it, so an unauthenticated read here would
         quietly empty this page for exactly the brand it exists to fix. */
      /* A TIMEOUT IS NOT OPTIONAL. axios defaults to none, so a stopped API
         server leaves this promise pending and the page shows "Loading
         products..." forever with nothing in the console to explain it. Same
         class of bug that made the mobile apps spin (see apiClient). */
      const response = await axios.get(
        `${API_URL}/api/brands/${brandId}/sections`,
        { headers: authHeaders, timeout: 20000 },
      );
      // API returns { success: true, data: [...] }
      const sectionsData = response.data?.data || response.data || [];
      setSections(Array.isArray(sectionsData) ? sectionsData : []);
    } catch (error) {
      console.error("Error fetching sections:", error);
      // Say which failure it was. "Failed to fetch sections" sends someone
      // looking at the brand; a timeout means the API is not answering at all.
      const offline =
        error.code === "ECONNABORTED" ||
        error.code === "ERR_NETWORK" ||
        !error.response;
      setLoadError(
        offline
          ? "The Lusuku API did not respond. Check that the server is running, then try again."
          : error?.response?.data?.message ||
              "This brand's products could not be loaded.",
      );
      setSections([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuSectionSubmit = async (e) => {
    e.preventDefault();
    if (!sectionTitle.trim()) {
      toast.error("Section title is required");
      return;
    }

    try {
      if (currentSectionId) {
        // Update section
        await axios.put(
          `${API_URL}/api/brands/${brandId}/sections/${currentSectionId}`,
          { title: sectionTitle, description: sectionDescription, brandId },
          { headers: authHeaders },
        );
        toast.success("Section updated successfully");
      } else {
        // Create section
        await axios.post(
          `${API_URL}/api/brands/${brandId}/sections`,
          { title: sectionTitle, description: sectionDescription },
          { headers: authHeaders },
        );
        toast.success("Section created successfully");
      }
      setShowCreateModal(false);
      setShowEditModal(false);
      resetForm();
      fetchSections();
    } catch (error) {
      console.error("Error saving section:", error);
      toast.error(error.response?.data?.message || "Failed to save section");
    }
  };

  const handleEditSection = (sectionId, title, description, brandId) => {
    setCurrentSectionId(sectionId);
    setSectionTitle(title);
    setSectionDescription(description || "");
    setShowEditModal(true);
  };

  const handleDeleteSection = (sectionId) => {
    setSectionToDelete(sectionId);
    setShowDeleteModal(true);
  };

  const confirmDeleteSection = async () => {
    try {
      await axios.delete(
        `${API_URL}/api/brands/${brandId}/sections/${sectionToDelete}`,
        {
          headers: authHeaders,
          data: {
            reason: "Section deleted with all contained brand products",
          },
        },
      );
      toast.success("Section and its products deleted successfully");
      setShowDeleteModal(false);
      setSectionToDelete(null);
      fetchSections();
    } catch (error) {
      console.error("Error deleting section:", error);
      toast.error("Failed to delete the section. Please try again.");
    }
  };

  const allProducts = useMemo(
    () =>
      (Array.isArray(sections) ? sections : []).flatMap(
        (x) => x.products || [],
      ),
    [sections],
  );

  const totals = useMemo(() => {
    const active = allProducts.filter(isProductActive).length;
    return {
      sections: Array.isArray(sections) ? sections.length : 0,
      products: allProducts.length,
      active,
      deactivated: allProducts.length - active,
      ...photoTotals(allProducts),
    };
  }, [sections, allProducts]);

  /* Sections with their products filtered. A section is kept when it still has a
     match, OR when the section title itself matches — searching "Freezers"
     should find the section even before you know what is in it. */
  const visibleSections = useMemo(() => {
    const q = query.trim().toLowerCase();
    const statusTest = STATUS_FILTERS.find((f) => f.key === statusFilter)?.test;
    const photoTest = photoFilterTest(photoFilter);
    if (!q && !photoTest && !statusTest) {
      return Array.isArray(sections) ? sections : [];
    }
    return (Array.isArray(sections) ? sections : [])
      .map((section) => {
        const titleHit =
          q &&
          String(section.title || "")
            .toLowerCase()
            .includes(q);
        const products = (section.products || []).filter((p) => {
          if (photoTest && !photoTest(p)) return false;
          if (statusTest && !statusTest(p)) return false;
          if (!q) return true;
          if (titleHit) return true;
          return (
            String(p.name || "")
              .toLowerCase()
              .includes(q) ||
            String(p.skuCode || "")
              .toLowerCase()
              .includes(q)
          );
        });
        return { ...section, products };
      })
      .filter((section) => section.products.length > 0);
  }, [sections, query, photoFilter, statusFilter]);

  const isFiltering = !!query.trim() || !!photoFilter || !!statusFilter;
  const matchCount = visibleSections.reduce(
    (n, section) => n + (section.products || []).length,
    0,
  );

  /* While filtering, every surviving section opens: a closed accordion hiding
     the one row you searched for is a search that did nothing. */
  useEffect(() => {
    if (isFiltering) {
      setActiveKey(visibleSections.map((section) => String(section._id)));
    }
  }, [isFiltering, visibleSections]);

  const expandAll = () =>
    setActiveKey(visibleSections.map((section) => String(section._id)));
  const collapseAll = () => setActiveKey([]);

  const handleAddProductToSection = (e, sectionId, brandId) => {
    e.preventDefault();
    history.push({
      pathname: "/admin/brand-product-create",
      state: { sectionId, brandId, brandName },
    });
  };

  // Quick per-item activate/deactivate — unified endpoint; storefronts update
  // live via the catalog:public `product:statusChanged` socket event.
  /**
   * Activate or deactivate every product in one section.
   *
   * A loop over the SAME audited per-item endpoint the switch uses, not a new
   * bulk route. A bulk endpoint would need its own permission checks, its own
   * audit semantics and its own socket emissions to do what this already does
   * correctly — and would be a second place for the rules to drift.
   *
   * Products already in the target state are skipped rather than re-sent, so
   * "Activate all" on a section that is 28/30 live costs two calls, not thirty.
   */
  const handleBulkSectionStatus = (section, next) => {
    const targets = (section.products || []).filter(
      (p) => isProductActive(p) !== next,
    );
    if (!targets.length) return;

    // Activating is additive and instantly reversible, so it just runs.
    // Hiding a whole section from shoppers is worth one deliberate beat.
    if (!next) {
      setBulkConfirm({ section, count: targets.length });
      return;
    }
    performBulkSectionStatus(section, next);
  };

  const performBulkSectionStatus = async (section, next) => {
    const targets = (section.products || []).filter(
      (p) => isProductActive(p) !== next,
    );
    if (!targets.length) return;

    setBulkBusySection(String(section._id));
    let done = 0;
    const failures = [];
    const unpriced = [];
    for (const product of targets) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await apiSetCatalogItemStatus("brand", product._id, next);
        done += 1;
      } catch (err) {
        // A product with no price yet stays off on purpose; say that apart
        // from real failures so nobody retries it.
        if (err?.response?.data?.code === "PRICE_REQUIRED") unpriced.push(product.name);
        else failures.push(product.name);
      }
    }
    setBulkBusySection(null);
    setBulkConfirm(null);

    if (done) {
      toast.success(
        `${done} product${done === 1 ? "" : "s"} in "${section.title}" ${
          next ? "activated" : "deactivated"
        }`,
      );
    }
    if (unpriced.length) {
      toast.warn(
        `${unpriced.length} still need${unpriced.length === 1 ? "s" : ""} a price before going live: ${unpriced
          .slice(0, 3)
          .join(", ")}${unpriced.length > 3 ? "…" : ""}`,
      );
    }
    if (failures.length) {
      // Name them. "3 failed" sends the operator hunting; the names do not.
      toast.error(
        `Could not change ${failures.length}: ${failures
          .slice(0, 3)
          .join(", ")}${failures.length > 3 ? "…" : ""}`,
      );
    }
    fetchSections();
  };

  const handleToggleProductStatus = async (e, product) => {
    e.stopPropagation();
    const next = !isProductActive(product);
    try {
      await apiSetCatalogItemStatus("brand", product._id, next);
      toast.success(`"${product.name}" ${next ? "activated" : "deactivated"}`);
      fetchSections();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to change product status",
      );
    }
  };

  /* ── Arranging products within a section ─────────────────────────────────
     The order an operator drags into is the order every storefront renders
     (the server sorts by sortOrder; see brandB2BPublic.sortSectionProducts),
     so the drop is applied to `sections` at once and then saved. A failed save
     puts the previous order back — the page must never show an arrangement
     shoppers are not getting. Arranging is switched off while a filter is
     active: dragging within a subset has no single honest meaning for the
     rows that are hidden. */
  // Vendor team permissions: arranging, adding, editing and deleting are
  // catalogue:edit; switching products on and off is catalogue:activate.
  // Owners and platform staff always pass (see components/team/Gated.js).
  const catalogueGate = useGate("catalogue:edit");
  const activateGate = useGate("catalogue:activate");
  const canArrange = !isFiltering && catalogueGate.allowed;
  const [drag, setDrag] = useState(null); // { sectionId, productId, overId, placeAfter }
  const [savingOrderFor, setSavingOrderFor] = useState(null);
  const [savedOrderFor, setSavedOrderFor] = useState(null);
  const savedNoteTimerRef = useRef(null);
  // Only a drag that STARTED on the grip is allowed to proceed; the row is
  // draggable so the whole row travels as the drag image, but a click-and-
  // drag on the name or the image must stay a click.
  const armedRef = useRef(null);
  // One save per burst of arrow-key moves, with the order from BEFORE the
  // burst kept for a revert.
  const saveTimerRef = useRef({});
  const baselineRef = useRef({});

  const productsOf = (sectionId) =>
    sections.find((s) => String(s._id) === String(sectionId))?.products || [];

  const setSectionProducts = (sectionId, products) =>
    setSections((prev) =>
      prev.map((s) =>
        String(s._id) === String(sectionId) ? { ...s, products } : s,
      ),
    );

  /* Reorder a product list to a server-confirmed id list: the ids it names
     first, in that order, then anything it did not name in their current
     order. Pure, so it can run inside a functional setState. */
  const applyOrder = (products, ids) => {
    const byId = new Map(products.map((p) => [String(p._id), p]));
    const listed = new Set(ids.map(String));
    return [
      ...ids.map((id) => byId.get(String(id))).filter(Boolean),
      ...products.filter((p) => !listed.has(String(p._id))),
    ];
  };

  /* THE ORDER TO SAVE IS PASSED IN, NEVER READ FROM STATE HERE. This runs
     from a debounce timer, and a timer callback closes over the render that
     scheduled it — whose `sections` is the order from BEFORE the move. The
     first version read state, saved the pre-move order, and the server's
     honest echo of it snapped the row straight back to where it started. */
  const persistSectionOrder = async (sectionId, orderedIds) => {
    const sid = String(sectionId);
    const previous = baselineRef.current[sid];
    delete baselineRef.current[sid];
    if (!Array.isArray(orderedIds) || orderedIds.length === 0) return;
    setSavingOrderFor(sid);
    try {
      const { data } = await axios.put(
        `${API_URL}/api/brands/${brandId}/sections/${sid}/products/order`,
        { productIds: orderedIds },
        { headers: authHeaders, timeout: 20000 },
      );
      // The server answers with the COMPLETE order it wrote — including any
      // product a colleague added since this page loaded — so take that as
      // the truth. Applied against the LATEST state (functional update), not
      // the products this closure remembers.
      const serverIds = data?.data?.productIds;
      if (Array.isArray(serverIds) && serverIds.length > 0) {
        setSections((prev) =>
          prev.map((s) =>
            String(s._id) === sid
              ? { ...s, products: applyOrder(s.products || [], serverIds) }
              : s,
          ),
        );
      }
      // A quiet, short-lived confirmation in the section itself — a toast per
      // arrow-key press would be noise.
      setSavedOrderFor(sid);
      clearTimeout(savedNoteTimerRef.current);
      savedNoteTimerRef.current = setTimeout(
        () => setSavedOrderFor((cur) => (cur === sid ? null : cur)),
        2200,
      );
    } catch (error) {
      if (previous) setSectionProducts(sid, previous);
      toast.error(
        error?.response?.data?.message ||
          "The new order could not be saved, so the previous order is back.",
      );
    } finally {
      setSavingOrderFor((cur) => (cur === sid ? null : cur));
    }
  };

  const scheduleOrderSave = (sectionId, orderedIds) => {
    const sid = String(sectionId);
    clearTimeout(saveTimerRef.current[sid]);
    saveTimerRef.current[sid] = setTimeout(
      () => persistSectionOrder(sid, orderedIds),
      450,
    );
  };

  /* Move one product to a new index within its section, then save. */
  const moveProduct = (sectionId, productId, toIndex) => {
    const sid = String(sectionId);
    const products = productsOf(sid);
    const fromIndex = products.findIndex((p) => String(p._id) === String(productId));
    const target = Math.max(0, Math.min(products.length - 1, toIndex));
    if (fromIndex < 0 || target === fromIndex) return;
    if (!baselineRef.current[sid]) baselineRef.current[sid] = products;
    const next = [...products];
    const [row] = next.splice(fromIndex, 1);
    next.splice(target, 0, row);
    setSectionProducts(sid, next);
    scheduleOrderSave(
      sid,
      next.map((p) => String(p._id)),
    );
  };

  const onGripKeyDown = (e, sectionId, productId, index) => {
    if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
    e.preventDefault();
    e.stopPropagation();
    moveProduct(sectionId, productId, index + (e.key === "ArrowUp" ? -1 : 1));
  };

  const onRowDragStart = (e, sectionId, productId) => {
    if (!canArrange || armedRef.current !== String(productId)) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.effectAllowed = "move";
    // Firefox will not start a drag with an empty payload.
    try {
      e.dataTransfer.setData("text/plain", String(productId));
    } catch (_) {
      /* IE-era quirk; nothing to do */
    }
    setDrag({ sectionId: String(sectionId), productId: String(productId), overId: null, placeAfter: false });
  };

  const onRowDragOver = (e, sectionId, productId) => {
    if (!drag || drag.sectionId !== String(sectionId)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const rect = e.currentTarget.getBoundingClientRect();
    const placeAfter = e.clientY > rect.top + rect.height / 2;
    const overId = String(productId);
    setDrag((d) =>
      d && (d.overId !== overId || d.placeAfter !== placeAfter)
        ? { ...d, overId, placeAfter }
        : d,
    );
  };

  const onRowDrop = (e, sectionId) => {
    if (!drag || drag.sectionId !== String(sectionId)) return;
    e.preventDefault();
    const products = productsOf(sectionId);
    const fromIndex = products.findIndex((p) => String(p._id) === drag.productId);
    const overIndex = products.findIndex((p) => String(p._id) === drag.overId);
    if (fromIndex >= 0 && overIndex >= 0) {
      let toIndex = overIndex + (drag.placeAfter ? 1 : 0);
      if (fromIndex < toIndex) toIndex -= 1;
      moveProduct(sectionId, drag.productId, toIndex);
    }
    setDrag(null);
    armedRef.current = null;
  };

  const onRowDragEnd = () => {
    setDrag(null);
    armedRef.current = null;
  };

  const handleEditProduct = (productId) => {
    const productToEdit = sections
      .flatMap((section) => section.products || [])
      .find((product) => product._id === productId);

    if (productToEdit) {
      history.push({
        pathname: `/admin/brand-product-edit/${productId}`,
        state: {
          productDetails: productToEdit,
          brandName,
          brandId: brandId, // Pass brandId explicitly
        },
      });
    } else {
      toast.error("Product not found!");
    }
  };

  const handleSectionClick = (eventKey) => {
    setActiveKey((prevActiveKey) =>
      prevActiveKey.includes(eventKey)
        ? prevActiveKey.filter((key) => key !== eventKey)
        : [...prevActiveKey, eventKey],
    );
  };

  const resetForm = () => {
    setSectionTitle("");
    setSectionDescription("");
    setCurrentSectionId(null);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    resetForm();
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    resetForm();
  };

  if (resolvingBrand) {
    return (
      <Col md="10">
        <Card className="menu-card-adorned">
          <Card.Header className="menu-header-adorned d-flex justify-content-between align-items-center">
            <div>
              <div
                style={{
                  width: "260px",
                  height: "28px",
                  borderRadius: "6px",
                  background: "rgba(255,255,255,0.15)",
                  marginBottom: "8px",
                  animation: "bpm-pulse 1.4s ease-in-out infinite",
                }}
              />
              <div
                style={{
                  width: "180px",
                  height: "14px",
                  borderRadius: "4px",
                  background: "rgba(255,255,255,0.1)",
                  animation: "bpm-pulse 1.4s ease-in-out infinite",
                }}
              />
            </div>
            <div
              style={{
                width: "110px",
                height: "36px",
                borderRadius: "8px",
                background: "rgba(255,255,255,0.15)",
                animation: "bpm-pulse 1.4s ease-in-out infinite",
              }}
            />
          </Card.Header>
          <Card.Body style={{ padding: "24px" }}>
            <style>{`
              @keyframes bpm-pulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.4; }
              }
            `}</style>
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                style={{
                  marginBottom: "16px",
                  borderRadius: "8px",
                  border: "1px solid #e9ecef",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "52px",
                    background: "#f1f5f9",
                    animation: "bpm-pulse 1.4s ease-in-out infinite",
                    animationDelay: `${i * 0.15}s`,
                  }}
                />
              </div>
            ))}
          </Card.Body>
        </Card>
      </Col>
    );
  }

  return (
    <Col md="10">
      <style>{customFontsCSS}</style>
      <Card className="menu-card-adorned">
        <Card.Header className="menu-header-adorned d-flex justify-content-between align-items-center bpm-head">
          <div className="bpm-head-text">
            <div className="d-flex align-items-center mb-2">
              <FaShoppingBag
                size={32}
                style={{ marginRight: "12px", flexShrink: 0 }}
              />
              <Card.Title
                as="h3"
                style={{
                  marginBottom: 0,
                  // 28px on a desktop, down to 22px on a phone, so a long
                  // brand name does not fill the whole first screen.
                  fontSize: "clamp(22px, 6vw, 28px)",
                  overflowWrap: "anywhere",
                  fontWeight: "700",
                  fontFamily: "'Inter', sans-serif",
                  color: "white",
                }}
              >
                Product Management - {brandName}
              </Card.Title>
            </div>
            <p style={{ marginBottom: 0, opacity: 0.9, fontSize: "14px" }}>
              Manage sections and products for this brand
            </p>
          </div>
          {/* Three actions of unequal weight, so they get unequal treatment:
              Add Section is what most visits are for and reads as the primary;
              Import is the occasional bulk path; the template is a file link,
              not really an action at all. Ranking them stops the row wrapping
              into three lines of identical white buttons. */}
          <div className="bpm-header-actions">
            <a
              href={TEMPLATE_URL}
              download="Lusuku_Brand_Import_Template.xlsx"
              className="bpm-header-link"
              title="Download the blank product workbook to fill in"
            >
              <FaDownload size={12} />
              <span>Template</span>
            </a>
            <Gated perm="catalogue:edit">
              <Button
                variant="light"
                onClick={() => setShowImportModal(true)}
                className="bpm-header-btn bpm-header-btn--ghost"
                title="Bulk upload products from a filled workbook"
              >
                <FaFileImport size={14} />
                <span>Import</span>
              </Button>
              <Button
                variant="light"
                onClick={() => setShowCreateModal(true)}
                className="bpm-header-btn bpm-header-btn--primary"
              >
                <MdAdd size={18} />
                <span>Add Section</span>
              </Button>
            </Gated>
          </div>
        </Card.Header>

        {/* Preview lives beside the editor rather than on another route: the
            point of it is to check a change you just made, so it has to be one
            click away from the row you changed. */}
        <div className="bpm-tabs">
          <button
            type="button"
            className={activeTab === "products" ? "is-on" : ""}
            onClick={() => setActiveTab("products")}
          >
            Products
          </button>
          <button
            type="button"
            className={activeTab === "simulation" ? "is-on" : ""}
            onClick={() => setActiveTab("simulation")}
          >
            Simulation
          </button>
          {branchDirectory.hasBranches && (
            <button
              type="button"
              className={activeTab === "branches" ? "is-on" : ""}
              onClick={() => setActiveTab("branches")}
            >
              Branches
            </button>
          )}
        </div>

        {/* What this catalogue contains, and how to find one row in seventy.
            Hidden on Simulation and while there is nothing to summarise. */}
        {activeTab === "products" &&
          !loading &&
          !loadError &&
          totals.sections > 0 && (
            <div className="bpm-toolbar">
              <div className="bpm-counts">
                <span>
                  <b>{totals.sections}</b> section
                  {totals.sections === 1 ? "" : "s"}
                </span>
                <span className="bpm-count-sep">·</span>
                <span>
                  <b>{totals.products}</b> product
                  {totals.products === 1 ? "" : "s"}
                </span>
                <span className="bpm-count-sep">·</span>
                <span title="Products a shopper can see, once the brand itself is active">
                  <b>{totals.active}</b> active
                </span>
              </div>

              <SearchBox
                className="bpm-search-wrap"
                value={query}
                onChange={setQuery}
                placeholder="Search products by name or SKU"
              />
              {isFiltering && (
                <SearchHits
                  shown={matchCount}
                  total={totals.products}
                  noun={matchCount === 1 ? "matches" : "match"}
                />
              )}

              <div className="bpm-filters">
                {STATUS_FILTERS.map((f) => {
                  const on = statusFilter === f.key;
                  const count = totals[f.key];
                  return (
                    <button
                      key={f.key}
                      type="button"
                      className={`bpm-tool-btn${on ? " is-on" : ""}`}
                      onClick={() => setStatusFilter(on ? null : f.key)}
                      disabled={!on && count === 0}
                      aria-pressed={on}
                      title={f.title}
                    >
                      {f.label} ({count})
                    </button>
                  );
                })}

                {/* Same two chips the pharmacy console renders, from the
                    same module, wearing this console's own chip class so
                    nothing here changed when they were shared. */}
                <PhotoFilterChips
                  value={photoFilter}
                  onChange={setPhotoFilter}
                  totals={totals}
                  className="bpm-tool-btn"
                />
              </div>

              <button
                type="button"
                className="bpm-tool-btn"
                onClick={expandAll}
                disabled={activeKey.length === visibleSections.length}
              >
                Expand all
              </button>
              <button
                type="button"
                className="bpm-tool-btn"
                onClick={collapseAll}
                disabled={activeKey.length === 0}
              >
                Collapse all
              </button>
            </div>
          )}

        <Card.Body className="bpm-body">
          {activeTab === "products" && branchDirectory.hasBranches && (
            <BranchMemberNote onOpenBranches={() => setActiveTab("branches")} />
          )}
          {activeTab === "branches" && branchDirectory.hasBranches ? (
            <BranchAvailabilityGrid
              vendorUserId={brandOwnerId}
              products={(Array.isArray(sections) ? sections : []).flatMap((s) => s.products || [])}
              onChanged={branchOff.refresh}
            />
          ) : activeTab === "simulation" ? (
            <ProductSimulation
              sections={Array.isArray(sections) ? sections : []}
              variant="brand"
              storeName={brandName}
            />
          ) : loading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: "300px" }}
            >
              <div className="text-center">
                <Spinner
                  animation="border"
                  role="status"
                  style={{ width: "3rem", height: "3rem", color: "#17385aff" }}
                >
                  <span className="sr-only">Loading...</span>
                </Spinner>
                <p
                  style={{
                    marginTop: "16px",
                    color: "#6c757d",
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  Loading products...
                </p>
              </div>
            </div>
          ) : loadError ? (
            /* A failed load is NOT an empty catalogue. Showing "No Sections
               Yet" after a timeout tells someone their products are gone and
               invites them to create duplicates on top of the ones that are
               still there. */
            <div className="empty-state">
              <div className="empty-state-icon" style={{ color: "#b3261e" }}>
                <MdErrorOutline size={64} />
              </div>
              <h4
                style={{ fontFamily: "'Inter', sans-serif", fontWeight: "600" }}
              >
                Could not load this catalogue
              </h4>
              <p style={{ maxWidth: 460, margin: "0 auto" }}>{loadError}</p>
              <div style={{ marginTop: 18 }}>
                <Button
                  className="btn-adorned"
                  onClick={fetchSections}
                  style={{
                    background: "#17385a",
                    border: "none",
                    color: "#fff",
                  }}
                >
                  Try again
                </Button>
              </div>
            </div>
          ) : !Array.isArray(sections) || sections.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">
                <MdShoppingBag size={64} />
              </div>
              <h4
                style={{ fontFamily: "'Inter', sans-serif", fontWeight: "600" }}
              >
                No Sections Yet
              </h4>
              <p style={{ maxWidth: 460, fontSize: "13px" }}>
                Create a section by hand, or import a filled workbook and let
                the sections build themselves.
              </p>
              <div
                style={{
                  display: "flex-start",
                  gap: "10px",
                  justifyContent: "center",
                  marginTop: "16px",
                  flexWrap: "wrap",
                }}
              >
                <Gated perm="catalogue:edit">
                <Button
                  className="btn-adorned"
                  onClick={() => setShowCreateModal(true)}
                  style={{
                    background: "#17385a",
                    border: "1px solid #17385a",
                    color: "#fff",
                    margin: "0 6px 6px 0",
                  }}
                >
                  Create Section
                </Button>
                {/* Explicit inline colours: the admin theme repaints Bootstrap's
                    outline variants grey-on-white, which reads as disabled. */}
                <Button
                  className="btn-adorned"
                  onClick={() => setShowImportModal(true)}
                  style={{
                    background: "#fff",
                    border: "1px solid #17385a",
                    color: "#17385a",
                  }}
                >
                  <FaFileImport size={14} style={{ marginRight: 7 }} />
                  Import Products
                </Button>
                </Gated>
              </div>
            </div>
          ) : isFiltering && visibleSections.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">
                <MdSearchOff size={64} />
              </div>
              <h4
                style={{ fontFamily: "'Inter', sans-serif", fontWeight: "600" }}
              >
                Nothing matches
              </h4>
              <p>
                No product in {totals.sections} section
                {totals.sections === 1 ? "" : "s"} matches
                {query.trim() ? ` "${query.trim()}"` : " that filter"}.
              </p>
              <div style={{ marginTop: 16 }}>
                <button
                  type="button"
                  className="bpm-tool-btn"
                  onClick={() => {
                    setQuery("");
                    setPhotoFilter(null);
                    setStatusFilter(null);
                  }}
                >
                  Clear the filters
                </button>
              </div>
            </div>
          ) : (
            <Accordion
              defaultActiveKey={activeKey}
              activeKey={activeKey}
              onSelect={handleSectionClick}
            >
              {visibleSections.map((section, index) => (
                <Accordion.Item
                  eventKey={String(section._id)}
                  key={section._id}
                  className="accordion-item-adorned"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <CustomAccordionHeader
                    section={section}
                    activeKey={activeKey}
                    handleEditSection={handleEditSection}
                    handleDeleteSection={handleDeleteSection}
                    handleAddProductToSection={handleAddProductToSection}
                    onToggle={handleSectionClick}
                    onBulkStatus={handleBulkSectionStatus}
                    bulkBusy={bulkBusySection === String(section._id)}
                    canEdit={catalogueGate.allowed}
                    canActivate={activateGate.allowed}
                    activateTitle={activateGate.title}
                  />
                  <Accordion.Body
                    onDragOver={(e) => {
                      if (drag && drag.sectionId === String(section._id)) e.preventDefault();
                    }}
                    onDrop={(e) => onRowDrop(e, section._id)}
                  >
                    {section.products && section.products.length > 1 && (
                      <div className="bpm-order-note" aria-live="polite">
                        {savingOrderFor === String(section._id) ? (
                          <b>Saving the new order…</b>
                        ) : savedOrderFor === String(section._id) ? (
                          <b>Order saved. Shoppers now see the products in this order.</b>
                        ) : canArrange ? (
                          "Drag the grip to arrange these products in the order shoppers should see them."
                        ) : (
                          `${ARRANGE_BLOCKED_HINT}.`
                        )}
                      </div>
                    )}
                    {section.products && section.products.length > 0 ? (
                      section.products.map((product, productIndex) => (
                        <div
                          key={product._id}
                          className={`bpm-row d-flex align-items-center justify-content-between${
                            drag && drag.productId === String(product._id) ? " is-dragging" : ""
                          }${
                            drag &&
                            drag.sectionId === String(section._id) &&
                            drag.overId === String(product._id) &&
                            drag.productId !== String(product._id)
                              ? drag.placeAfter
                                ? " drop-after"
                                : " drop-before"
                              : ""
                          }`}
                          style={{
                            marginBottom: "10px",
                            borderBottom: "1px solid #ccc",
                            paddingBottom: "10px",
                            cursor: "pointer",
                          }}
                          draggable={canArrange && section.products.length > 1}
                          onDragStart={(e) => onRowDragStart(e, section._id, product._id)}
                          onDragOver={(e) => onRowDragOver(e, section._id, product._id)}
                          onDragEnd={onRowDragEnd}
                          onClick={() => handleEditProduct(product._id)}
                        >
                          <div className="d-flex align-items-center bpm-row-main">
                            {section.products.length > 1 && (
                              <button
                                type="button"
                                className="bpm-grip"
                                disabled={!canArrange}
                                aria-label={`Arrange ${product.name}: drag, or press the up and down arrow keys`}
                                title={
                                  canArrange
                                    ? "Drag to arrange, or use ↑ ↓"
                                    : catalogueGate.allowed
                                      ? ARRANGE_BLOCKED_HINT
                                      : catalogueGate.title
                                }
                                onMouseDown={() => {
                                  armedRef.current = String(product._id);
                                }}
                                onMouseUp={() => {
                                  if (!drag) armedRef.current = null;
                                }}
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) =>
                                  onGripKeyDown(e, section._id, product._id, productIndex)
                                }
                              >
                                <MdDragIndicator size={20} />
                              </button>
                            )}
                            <div
                              className="bpm-img-wrap"
                              style={{ marginRight: "10px" }}
                            >
                              <img
                                src={product.image || placeholderImage}
                                alt={product.name}
                                style={{
                                  width: "50px",
                                  height: "50px",
                                  borderRadius: "20%",
                                  display: "block",
                                }}
                              />
                              <div className="bpm-img-popover">
                                <img
                                  src={product.image || placeholderImage}
                                  alt={product.name}
                                />
                              </div>
                            </div>
                            <div className="bpm-row-text">
                              <h6 style={{ marginBottom: 2 }}>
                                {product.name}
                                <NeedsPhotoBadge product={product} />
                                <BranchOffChip
                                  count={branchOff.counts.get(String(product._id))}
                                  total={branchOff.total}
                                />
                              </h6>
                              {product.skuCode && (
                                <div className="bpm-sku">{product.skuCode}</div>
                              )}
                              <div
                                style={{
                                  marginBottom: 0,
                                  fontSize: "12px",
                                }}
                              >
                                <ReadOnlyQuill
                                  value={product.description || ""}
                                />
                              </div>
                              {!isProductActive(product) && (
                                <p
                                  style={{
                                    marginBottom: 0,
                                    fontSize: "12px",
                                    color: "red",
                                  }}
                                >
                                  (Deactivated)
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="d-flex align-items-center bpm-row-side">
                            <div
                              style={{
                                fontSize: "14px",
                                fontFamily: "monospace",
                                fontWeight: "lighter",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {new Intl.NumberFormat("en-UG", {
                                style: "currency",
                                currency: "UGX",
                              }).format(product.price)}
                            </div>
                            <span
                              onClick={(e) => e.stopPropagation()}
                              title={
                                !activateGate.allowed
                                  ? activateGate.title
                                  : isProductActive(product)
                                    ? "Deactivate this item (hides it from shoppers instantly)"
                                    : "Activate this item (shoppers see it instantly)"
                              }
                            >
                              <Switch
                                size="small"
                                checked={isProductActive(product)}
                                disabled={!activateGate.allowed}
                                onChange={(e) =>
                                  handleToggleProductStatus(e, product)
                                }
                                color="primary"
                              />
                            </span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div>
                        No products found. Click "Add product" to create one.
                      </div>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          )}
        </Card.Body>
      </Card>

      {/* Bulk import. `existingSections` is what makes a re-run safe: the
          parser matches incoming rows against what the brand already has and
          skips them, so a half-finished import is resumed by uploading the
          same workbook again. */}
      <ConfirmDialog
        show={!!bulkConfirm}
        tone="warning"
        title="Hide this section from shoppers?"
        body={
          <>
            {bulkConfirm?.count} product
            {bulkConfirm?.count === 1 ? "" : "s"} in{" "}
            <strong>{bulkConfirm?.section?.title}</strong> will stop being
            visible to customers.
          </>
        }
        note="Nothing is deleted. You can activate them again at any time, one by one or all at once."
        confirmLabel="Deactivate them"
        busyLabel="Deactivating…"
        busy={
          !!bulkConfirm && bulkBusySection === String(bulkConfirm.section?._id)
        }
        onConfirm={() => performBulkSectionStatus(bulkConfirm.section, false)}
        onCancel={() => setBulkConfirm(null)}
      />

      <BrandImportModal
        show={showImportModal}
        brand={{ _id: brandId, name: brandName }}
        existingSections={Array.isArray(sections) ? sections : []}
        onClose={() => setShowImportModal(false)}
        onImported={fetchSections}
      />

      {/* Create Section Modal */}
      <Modal show={showCreateModal} onHide={handleCloseCreateModal}>
        <Modal.Header closeButton>
          <Modal.Title>Create New Section</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleMenuSectionSubmit}>
            <Form.Group controlId="sectionTitle">
              <Form.Label>Section Title *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter section title..."
                value={sectionTitle}
                onChange={(e) => setSectionTitle(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group controlId="sectionDescription" className="mt-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter section description..."
                value={sectionDescription}
                onChange={(e) => setSectionDescription(e.target.value)}
              />
            </Form.Group>
            <div className="d-flex justify-content-end gap-2 mt-3">
              <Button variant="secondary" onClick={handleCloseCreateModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Create Section
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Section Modal */}
      <Modal show={showEditModal} onHide={handleCloseEditModal}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Section</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleMenuSectionSubmit}>
            <Form.Group controlId="editSectionTitle">
              <Form.Label>Section Title *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter section title..."
                value={sectionTitle}
                onChange={(e) => setSectionTitle(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group controlId="editSectionDescription" className="mt-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter section description..."
                value={sectionDescription}
                onChange={(e) => setSectionDescription(e.target.value)}
              />
            </Form.Group>
            <div className="d-flex justify-content-end gap-2 mt-3">
              <Button variant="secondary" onClick={handleCloseEditModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Update Section
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this section and all its products?
          This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDeleteSection}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Col>
  );
};

export default BrandProductManagement;
